import { memo, useMemo, type JSX } from 'react';
import type { Product } from '@/types/Product.types';
import styles from './DoubleColumnProductGrid.module.css';
import type {
  ColumnData,
  DoubleColumnProductGridProps,
} from './DoubleColumnProductGrid.types';

const DoubleColumnProductGrid = ({
  products,
  renderProductItem,
  className = '',
  paddingBetween,
  paddingBottom,
}: DoubleColumnProductGridProps): JSX.Element => {
  const { colOneData, colTwoData } = useMemo(() => {
    if (!products || !Array.isArray(products))
      return { colOneData: [], colTwoData: [] };

    return products.reduce<ColumnData>(
      (acc, product) => {
        if (acc.colOneData.length <= acc.colTwoData.length) {
          acc.colOneData.push(product);
        } else {
          acc.colTwoData.push(product);
        }
        return acc;
      },
      { colOneData: [], colTwoData: [] }
    );
  }, [products]);

  const renderColumn = (
    data: Product[],
    isFirstColumn: boolean
  ): JSX.Element => (
    <div className={styles.column}>
      {data.map((product, index) => (
        <div
          key={`col${isFirstColumn ? '1' : '2'}-${index}`}
          style={{
            paddingBottom:
              data.length - 1 === index ? paddingBottom : paddingBetween,
          }}
        >
          {renderProductItem(product)}
        </div>
      ))}
    </div>
  );

  return (
    <div className={`${styles.content} ${className}`}>
      {renderColumn(colOneData, true)}
      {renderColumn(colTwoData, false)}
    </div>
  );
};

export default memo(DoubleColumnProductGrid);
